import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Layout from '../../components/ui/Layout';
import Table from '../../components/ui/Table';
import ActionDropdown from '../../components/ui/ActionDropdown';
import SearchFilterCard, { FilterConfig } from '../../components/ui/SearchFilterCard';
import BulkActionsBar from '../../components/ui/BulkActionsBar';
import {
  RiBuildingLine,
  RiRefreshLine,
  RiDeleteBin2Line,
  RiFileDownloadLine,
  RiFilterLine,
  RiEyeLine,
  RiEditLine,
  RiDeleteBinLine,
  RiFileTextLine
} from 'react-icons/ri';
import companyService, { Company } from '../../services/companyService';
import userService from '../../services/userService';
import { useUserPageSize } from '../../hooks/useUserPageSize';
import { showAlert, showConfirm } from '../../utils/alert';
import { getApiErrorMessageAsync } from '../../utils/mapFieldErrors';
import { formatDateTimeFull, getLocalDateString } from '../../utils/dateUtils';
import { downloadExcelFile } from '../../utils/downloadExcel';
import AdvancedSearchModal, { SearchField } from '../../components/search/AdvancedSearchModal';
import useAdvancedSearch from '../../hooks/useAdvancedSearch';
import useDataSync from '../../hooks/useDataSync';
import { queryKeys } from '../../queryClient';
import { useDeleteCompany, useSyncCompanies } from '../../hooks/mutations/useCompanyMutations';
import { checkApiHealth } from '../../utils/apiWithLogging';
import Pagination from '../../components/ui/Pagination';
import { PAGINATION_CONFIG } from '../../config/pagination';
import { CanCreate } from '../../components/auth/PermissionGuard';
import { detailNavState } from '../../components/detail/detailNavigation';
import { COMPANIES_PATH, companyDetailUrl, companyEditUrl } from '../../utils/companyLinks';
import { usePermission } from '../../hooks/usePermission';
import LoadingOverlayModal from '../../components/ui/LoadingOverlayModal';
import { useClampPage } from '../../hooks/useClampPage';
import { runBulkDelete, bulkDeleteFailureMessage } from '../../utils/bulkDelete';

const CompanyPage: React.FC = () => {
  const { canView, canEdit, canDelete, hasPermission, canExport } = usePermission();
  const navigate = useNavigate();
  const location = useLocation();
  // ดู/เพิ่ม/แก้ไขเป็นหน้าเต็ม (มติ PO 28 ต.ค. 2026 · เดิมเป็นโมดัลในหน้านี้) — แนบ URL รายการ (ตัวกรอง/หน้า/คำค้น)
  // ไปกับ state ให้ปุ่ม "กลับ" ของหน้ารายละเอียดพากลับมาที่เดิม
  const openCompanyPage = (path: string) => navigate(path, { state: detailNavState(location) });

  // S1111 URL-sync: read URL params for lazy init (filter+page bidirectional)
  // filterStatus internal values: '' (all) / 'true' (active) / 'false' (inactive)
  // URL values: 'active' / 'inactive' for user-friendliness — map both directions
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPageParam = searchParams.get('page');
  const urlStatusParam = searchParams.get('status') || '';
  const urlToFilterStatus = (raw: string): string => {
    if (raw === 'active') return 'true';
    if (raw === 'inactive') return 'false';
    return '';
  };
  const filterStatusToUrl = (raw: string): string => {
    if (raw === 'true') return 'active';
    if (raw === 'false') return 'inactive';
    return '';
  };

  // State สำหรับ pagination และ search
  // KD3 S2495 (FLOW-MD-006): คำค้นอยู่ใน URL (?q=) แบบเดียวกับหน้าโครงการ/อุปกรณ์ (S2482) — เดิม refresh/Back แล้วคำค้นหาย
  // แต่ผู้ใช้ยังอยู่หน้าเดิม เห็นรายการทั้งหมดโดยไม่รู้ตัว
  const [searchTerm, setSearchTerm] = useState<string>(() => searchParams.get('q') || '');
  const [currentPage, setCurrentPage] = useState<number>(() => {
    if (!urlPageParam) return 1;
    const n = parseInt(urlPageParam, 10);
    if (!Number.isFinite(n) || n < 1) return 1;
    return Math.min(n, 9999);
  });
  const [pageSize, setPageSize] = useState<number>(PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
  const [advancedFilters, setAdvancedFilters] = useState<any>({});

  // State สำหรับ quick filters
  // S1111 URL-sync: hydrate from URL via mapper
  const [filterStatus, setFilterStatus] = useState<string>(urlToFilterStatus(urlStatusParam));

  // S1111 URL-sync: write filter+page to URL (Variant A overwrite — quick-filter URL only)
  const syncFiltersToUrl = useCallback((status: string, page: number, search: string) => {
    const newParams = new URLSearchParams();
    const mapped = filterStatusToUrl(status);
    if (mapped) newParams.set('status', mapped);
    if (search) newParams.set('q', search);
    if (page > 1) newParams.set('page', String(page));
    setSearchParams(newParams, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    syncFiltersToUrl(filterStatus, currentPage, searchTerm);
  }, [filterStatus, currentPage, searchTerm, syncFiltersToUrl]);
  
  // Mutation hooks
  const deleteCompanyMutation = useDeleteCompany();
  const syncCompaniesMutation = useSyncCompanies();
  
  const [selectedCompanies, setSelectedCompanies] = useState<number[]>([]);
  const [syncLoading, setSyncLoading] = useState<boolean>(false);
  const [bulkLoading, setBulkLoading] = useState<boolean>(false);
  
  // State สำหรับ users (sales persons) — ตัวเลือก "พนักงานขายที่ดูแล" ของค้นหาขั้นสูง
  const [users, setUsers] = useState<any[]>([]);
  
  // React Query for data fetching
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [...queryKeys.companies.list(), {
      page: currentPage,
      limit: pageSize,
      search: searchTerm,
      filterStatus,
      ...advancedFilters
    }],
    queryFn: async () => {
      const params: any = {
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
        ...advancedFilters
      };

      // เพิ่ม is_active filter ถ้ามีการเลือก status
      if (filterStatus !== '') {
        params.is_active = filterStatus === 'true';
      }

      return await companyService.getCompanies(params);
    },
  });
  
  // ข้อมูลบริษัทจาก React Query
  const companies = data?.items || [];
  const totalItems = data?.total || 0;
  useClampPage(currentPage, data?.total, pageSize, setCurrentPage, isLoading);

  // คำนวณสถิติบริษัท — ใช้ aggregate stats จาก backend (นับทุกหน้า ไม่ใช่เฉพาะหน้าปัจจุบัน
  // — user feedback amintra 2026-08-25) · fallback นับจากหน้าปัจจุบันถ้า backend ยังไม่ส่ง stats
  const companyStats = useMemo(() => {
    const activeCount = data?.stats?.active
      ?? companies.filter((c: Company) => c.isActive !== false && c.is_active !== false).length;
    const inactiveCount = data?.stats?.inactive ?? (companies.length - activeCount);
    const syncedCount = data?.stats?.synced
      ?? companies.filter((c: Company) => c.syncedFromFlow).length;
    const withDepositCount = companies.filter((c: Company) => (c.deposit_amount || 0) > 0).length;

    return {
      total: totalItems,
      active: activeCount,
      inactive: inactiveCount,
      synced: syncedCount,
      withDeposit: withDepositCount
    };
  }, [data, companies, totalItems]);

  // Loading state จาก mutations
  const loading = deleteCompanyMutation.isPending ||
                 syncCompaniesMutation.isPending;
  
  // FINDING-S980-A FIX (refactored S1035): consume itemsPerPage from user settings on mount
  // Shared hook applied across list pages — see hooks/useUserPageSize.ts
  useUserPageSize(pageSize, setPageSize);

  // Function to fetch sales persons (for the "พนักงานขายที่ดูแล" dropdown)
  // ดึงเฉพาะ user ที่อยู่ใต้ Role 'Sales' ผ่าน /users/sales-persons (gate companies.view —
  // ไม่ใช้ /users/ ที่ต้อง users.view ซึ่ง Sales ไม่มี). คืน id/full_name/username (ตรงกับที่ dropdown map).
  // ฟอร์มเพิ่ม/แก้ไขย้ายไป CompanyFormPage แล้ว — ที่นี่เหลือใช้กับค้นหาขั้นสูง
  const fetchUsers = async () => {
    try {
      const response = await userService.getSalesPersons();
      setUsers(response);
    } catch (error) {
      console.error('Error fetching sales persons:', error);
    }
  };
  
  // Advanced search configuration
  const searchFields: SearchField[] = [
    {
      key: 'name',
      label: 'ชื่อบริษัท',
      type: 'text',
      placeholder: 'ค้นหาตามชื่อบริษัท'
    },
    {
      key: 'code',
      label: 'รหัสบริษัท',
      type: 'text',
      placeholder: 'ค้นหาตามรหัสบริษัท'
    },
    {
      key: 'taxId',
      label: 'เลขประจำตัวผู้เสียภาษี',
      type: 'text',
      placeholder: 'ค้นหาตามเลขประจำตัวผู้เสียภาษี'
    },
    {
      key: 'contactPerson',
      label: 'ผู้ติดต่อ',
      type: 'text',
      placeholder: 'ค้นหาตามชื่อผู้ติดต่อ'
    },
    {
      key: 'phone',
      label: 'เบอร์โทรศัพท์',
      type: 'text',
      placeholder: 'ค้นหาตามเบอร์โทรศัพท์'
    },
    {
      key: 'email',
      label: 'อีเมล',
      type: 'text',
      placeholder: 'ค้นหาตามอีเมล'
    },
    {
      key: 'createdDate',
      label: 'วันที่สร้าง',
      type: 'dateRange'
    },
    {
      key: 'syncedFromFlow',
      label: 'ซิงค์จาก Flow Account',
      type: 'boolean'
    },
    {
      key: 'is_active',
      label: 'สถานะการใช้งาน',
      type: 'select',
      options: [
        { value: 'true', label: 'ใช้งาน' },
        { value: 'false', label: 'ไม่ใช้งาน' }
      ]
    },
    {
      key: 'sales_person_id',
      label: 'พนักงานขายที่ดูแล',
      type: 'select',
      options: users.map(user => ({
        value: user.id.toString(),
        label: user.full_name || user.username
      }))
    }
  ];

  // Advanced search hook
  const advancedSearch = useAdvancedSearch({
    searchFields,
    onSearch: (filters) => {
      
      // แปลง date range filters
      const processedFilters: any = {};
      
      Object.keys(filters).forEach(key => {
        if (key === 'createdDate' && filters[key]) {
          // แปลง date range เป็น created_from และ created_to
          if (filters[key].from) {
            processedFilters.created_from = filters[key].from;
          }
          if (filters[key].to) {
            processedFilters.created_to = filters[key].to;
          }
        } else if (key === 'syncedFromFlow') {
          // แปลง boolean value
          processedFilters.synced_from_flow = filters[key];
        } else if (key === 'contactPerson') {
          // แปลง field name ให้ตรงกับ backend
          processedFilters.contact_person = filters[key];
        } else if (key === 'taxId') {
          // แปลง field name ให้ตรงกับ backend
          processedFilters.tax_id = filters[key];
        } else if (filters[key] !== undefined && filters[key] !== '') {
          // ส่ง field อื่นๆ ตามปกติ
          processedFilters[key] = filters[key];
        }
      });
      
      // Clear general search term เมื่อใช้ advanced search
      setSearchTerm('');
      setAdvancedFilters(processedFilters);
      setCurrentPage(1);
    },
    storageKey: 'company_search_filters'
  });

  // KD3 S2495 (FLOW-MD-004): ตัวกรอง "พนักงานขายที่ดูแล" ในค้นหาขั้นสูงใช้รายชื่อชุดเดียวกัน แต่เดิมโหลดเฉพาะตอนเปิดฟอร์ม
  // → ผู้ใช้ที่ยังไม่เคยเปิดฟอร์มเห็นแต่ "ทั้งหมด" กรองตามพนักงานขายไม่ได้เลย · โหลดตอนเปิดค้นหาขั้นสูงด้วย (ครั้งเดียว)
  useEffect(() => {
    if (advancedSearch.isSearchModalOpen && users.length === 0) {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedSearch.isSearchModalOpen]);

  // Data sync hook สำหรับ real-time updates
  const { sendDataUpdate } = useDataSync({
    entityType: 'company',
    onEntityCreated: (company) => {
      // รีเฟรชข้อมูลเมื่อมีบริษัทใหม่
      refetch();
    },
    onEntityUpdated: (company) => {
      // รีเฟรชข้อมูลผ่าน React Query
      refetch();
    },
    onEntityDeleted: (companyId) => {
      // รีเฟรชข้อมูลผ่าน React Query
      refetch();
    },
    enableNotifications: true
  });
  
  // ตัวบ่งชี้สถานะการโหลดข้อมูล
  const [apiError, setApiError] = useState<string | null>(null);
  const [apiAvailable, setApiAvailable] = useState<boolean | null>(null);
  
  // ตรวจสอบว่า API พร้อมใช้งานหรือไม่
  useEffect(() => {
    const checkApiStatus = async () => {
      try {
        const isAvailable = await checkApiHealth();
        setApiAvailable(isAvailable);
        
        if (!isAvailable) {
          setApiError('ไม่สามารถเชื่อมต่อกับ API ได้ กรุณาตรวจสอบว่าเซิร์ฟเวอร์กำลังทำงานอยู่');
        }
      } catch (error) {
        console.error('Error checking API health:', error);
        setApiAvailable(false);
      }
    };
    
    checkApiStatus();
  }, []);
  
  // โหลดข้อมูลเมื่อคอมโพเนนต์ถูกโหลด
  useEffect(() => {
    // ถ้า API พร้อมใช้งานหรือยังไม่ได้ตรวจสอบ ให้ดึงข้อมูล
    if (apiAvailable !== false) {
      refetch();
    }
    
  }, [currentPage, searchTerm, apiAvailable, refetch]);
  
  // ฟังก์ชันสำหรับลบบริษัท
  const handleDeleteCompany = (company: Company) => {
    showConfirm(
      'ยืนยันการลบบริษัท',
      `คุณต้องการลบบริษัท ${company.name} ใช่หรือไม่? การกระทำนี้ไม่สามารถเปลี่ยนกลับได้ และอาจส่งผลกระทบต่อข้อมูลที่เกี่ยวข้อง`,
      async () => {
        try {
          // ใช้ mutation เพื่อลบบริษัท
          const result = await deleteCompanyMutation.mutateAsync(company.id);
          
          // ตรวจสอบผลการลบ (API จะส่ง success: true/false แทนที่จะส่ง error)
          if (result && result.success === true) {
            
            // ส่ง data update notification
            sendDataUpdate('delete', company.id);
            
            // React Query จะ refetch อัตโนมัติ
            refetch();
          } else if (result && result.success === false) {
            // กรณีไม่สามารถลบได้แต่ไม่มี error
            showAlert('ไม่สามารถลบบริษัท', result.message || 'ไม่สามารถลบบริษัทได้', 'warning');
          }
        } catch (error: any) {
          console.error('Error deleting company:', error);
          
          // useMutationWithRefresh จะจัดการแสดง error message แล้ว
          // ไม่ต้องแสดง error alert ซ้ำที่นี่
        }
      },
      { isDanger: true, confirmText: 'ลบ', cancelText: 'ยกเลิก' }
    );
  };
  
  // ฟังก์ชันสำหรับการซิงค์ข้อมูลกับ Flow Account
  const handleSyncWithFlowAccount = async () => {
    showConfirm(
      'ยืนยันการซิงค์ข้อมูล',
      'คุณต้องการซิงค์ข้อมูลกับ Flow Account ใช่หรือไม่?\n\nกระบวนการนี้อาจใช้เวลา 1-2 นาที โปรดรอสักครู่',
      async () => {
        setSyncLoading(true);
        
        // แสดง loading message แบบพิเศษสำหรับ sync
        showAlert('กำลังประมวลผล', 'กำลังซิงค์ข้อมูลกับ Flow Account กรุณารอสักครู่...', 'info');

        try {
          // เรียก API เพื่อซิงค์ข้อมูลกับ Flow Account
          const result = await companyService.syncWithFlowAccount();
          showAlert('สำเร็จ', `ซิงค์ข้อมูลกับ Flow Account สำเร็จ: ${result.message}`, 'success');

          // โหลดข้อมูลใหม่หลังจากซิงค์สำเร็จ
          refetch();
        } catch (error) {
          console.error('Error syncing with Flow Account:', error);
          showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถซิงค์ข้อมูลกับ Flow Account ได้ กรุณาลองใหม่อีกครั้ง', 'error');
        } finally {
          setSyncLoading(false);
        }
      },
      { confirmText: 'ซิงค์ข้อมูล', cancelText: 'ยกเลิก' }
    );
  };
  
  // ฟังก์ชันสำหรับการเลือกบริษัททั้งหมด
  // KD3 S2515: เปลี่ยนตัวกรอง/หน้าแล้วต้องล้างการเลือก — ไม่งั้นแถบ "เลือกแล้ว N" ค้าง และ "ลบที่เลือก" ลบแถวที่ผู้ใช้มองไม่เห็นแล้ว
  useEffect(() => {
    setSelectedCompanies([]);
  }, [currentPage, pageSize, searchTerm, advancedFilters, filterStatus]);

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedCompanies(companies.map(company => company.id));
    } else {
      setSelectedCompanies([]);
    }
  };
  
  // ฟังก์ชันสำหรับการเลือกบริษัทแต่ละรายการ
  const handleSelectCompany = (companyId: number, selected: boolean) => {
    if (selected) {
      setSelectedCompanies(prev => [...prev, companyId]);
    } else {
      setSelectedCompanies(prev => prev.filter(id => id !== companyId));
    }
  };
  
  // ฟังก์ชันสำหรับการลบบริษัทหลายรายการ
  const handleBulkDelete = async () => {
    if (selectedCompanies.length === 0) {
      showAlert('เลือกข้อมูล', 'กรุณาเลือกบริษัทที่ต้องการลบ', 'info');
      return;
    }
    
    showConfirm(
      'ยืนยันการลบบริษัท',
      `คุณต้องการลบบริษัท ${selectedCompanies.length} รายการที่เลือก ใช่หรือไม่? การกระทำนี้ไม่สามารถเปลี่ยนกลับได้`,
      async () => {
        setBulkLoading(true);
        try {
          // BUG-S2103-002: allSettled — รายการที่ลบไม่ได้ต้องบอกเหตุผล · ตารางต้อง refetch · เลือกค้างเฉพาะที่ลบไม่ได้
          const result = await runBulkDelete(selectedCompanies, id => companyService.deleteCompany(id));
          setSelectedCompanies(result.failed.map(f => f.id));
          refetch();
          if (result.failed.length === 0) {
            showAlert('สำเร็จ', `ลบบริษัท ${result.succeeded.length} รายการสำเร็จ`, 'success');
          } else {
            showAlert(result.succeeded.length ? 'ลบได้บางรายการ' : 'ลบไม่สำเร็จ', bulkDeleteFailureMessage('บริษัท', result), result.succeeded.length ? 'warning' : 'error');
          }
        } catch (error) {
          console.error('Error bulk deleting:', error);
          showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถลบรายการที่เลือกได้', 'error');
        } finally {
          setBulkLoading(false);
        }
      },
      { isDanger: true, confirmText: 'ลบ', cancelText: 'ยกเลิก' }
    );
  };
  
  // ฟังก์ชันสำหรับ Refresh
  const handleRefresh = () => {
    setSearchTerm('');
    setCurrentPage(1);
    setFilterStatus('');
    setAdvancedFilters({});
    setSelectedCompanies([]);
    advancedSearch.handleClearFilters();
    refetch();
  };

  // State สำหรับ Export Loading
  const [isExporting, setIsExporting] = useState(false);

  // ฟังก์ชันสำหรับการ export ข้อมูลเป็น Excel
  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      // ส่งตัวกรองชุดเดียวกับที่หน้ารายการใช้ — ไฟล์ต้องได้เฉพาะแถวที่เห็นบนจอ
      // ชุดนี้ต้องตรงกับ params ของ list query ด้านบน (search + is_active + advancedFilters)
      const blob = await companyService.exportToExcel({
        search: searchTerm || undefined,
        ...advancedFilters,
        ...(filterStatus !== '' ? { is_active: filterStatus === 'true' } : {}),
      });
      downloadExcelFile(blob, `companies_${getLocalDateString()}.xlsx`);
      showAlert('สำเร็จ', 'ส่งออกข้อมูลเรียบร้อยแล้ว', 'success');
    } catch (error) {
      console.error('Error exporting data:', error);
      showAlert('เกิดข้อผิดพลาด', await getApiErrorMessageAsync(error, 'ไม่สามารถส่งออกข้อมูลได้'), 'error');
    } finally {
      setIsExporting(false);
    }
  };

  // Filter configuration สำหรับ SearchFilterCard
  const filters: FilterConfig[] = [
    {
      key: 'status',
      label: 'สถานะการใช้งาน',
      type: 'select',
      options: [
        { value: 'true', label: 'ใช้งาน' },
        { value: 'false', label: 'ไม่ใช้งาน' }
      ],
      placeholder: 'ทั้งหมด',
      tagColor: 'success'
    }
  ];

  // Filter values object
  const filterValues = {
    status: filterStatus
  };

  // Handle filter change
  const handleFilterChange = (key: string, value: any) => {
    if (key === 'status') {
      setFilterStatus(value);
      setCurrentPage(1);
    }
  };

  // Handle clear all filters
  const handleClearAllFilters = () => {
    setSearchTerm('');
    setFilterStatus('');
    setCurrentPage(1);
  };

  // คอลัมน์ของตาราง
  const columns = [
    {
      key: 'select',
      title: (
        <div className="flex items-center justify-center">
          <input
            type="checkbox"
            aria-label="เลือกบริษัททั้งหมด"
            checked={selectedCompanies.length === companies.length && companies.length > 0}
            onChange={(e) => handleSelectAll(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
        </div>
      ),
      width: '40px',
      render: (value: any, record: Company) => (
        <input
          type="checkbox"
          aria-label={`เลือกบริษัท ${record.name || record.code || record.id}`}
          checked={selectedCompanies.includes(record.id)}
          onChange={(e) => handleSelectCompany(record.id, e.target.checked)}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
      ),
    },
    {
      key: 'code',
      title: 'รหัสบริษัท',
      width: '120px',
      sortable: true,
      render: (value: string) => (
        <span className="inline-flex items-center font-mono text-xs bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 px-2.5 py-1 rounded-lg border border-primary-200/50 shadow-sm">
          {value || '-'}
        </span>
      ),
    },
    {
      key: 'name',
      title: 'ชื่อบริษัท',
      width: '200px',
      sortable: true,
      render: (value: string, record: Company) => (
        <div className="flex items-center group">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-secondary-100 to-secondary-200 flex items-center justify-center mr-2.5 shadow-sm group-hover:shadow transition-shadow">
            <span className="text-xs font-bold text-secondary-600">
              {(value || '?').charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="truncate font-medium text-secondary-800 group-hover:text-primary-600 transition-colors" title={value}>
            {value || '-'}
          </div>
        </div>
      ),
    },
    {
      key: 'taxId',
      title: 'เลขผู้เสียภาษี',
      width: '140px',
      sortable: true,
      render: (value: string) => (
        <span className="inline-flex items-center font-mono text-xs text-secondary-600 bg-secondary-50 px-2 py-0.5 rounded-md">
          {value || '-'}
        </span>
      ),
    },
    {
      key: 'contactPerson',
      title: 'ผู้ติดต่อ',
      width: '120px',
      sortable: true,
      render: (value: string) => (
        <div className="truncate" title={value}>
          {value || '-'}
        </div>
      ),
    },
    {
      key: 'sales_person',
      title: 'พนักงานขาย',
      width: '120px',
      sortable: false,
      render: (_: any, record: Company) => (
        <div className="truncate" title={record.sales_person?.full_name || record.sales_person?.username || '-'}>
          {record.sales_person?.full_name || record.sales_person?.username || '-'}
        </div>
      ),
    },
    {
      key: 'isActive',
      title: 'สถานะ',
      width: '100px',
      sortable: true,
      render: (_: any, record: Company) => {
        // ใช้ isActive จาก backend หรือ is_active ถ้ามี
        const isActive = record.isActive !== undefined ? record.isActive : (record.is_active ?? true);
        return (
          <div className={`
            inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold
            transition-all duration-200 cursor-default
            ${isActive
              ? 'bg-gradient-to-r from-success-50 to-success-100 text-success-700 border border-success-200/50 shadow-sm shadow-success-100'
              : 'bg-gradient-to-r from-secondary-50 to-secondary-100 text-secondary-600 border border-secondary-200/50 shadow-sm shadow-secondary-100'
            }
          `}>
            <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-success-500 animate-pulse' : 'bg-secondary-400'}`} />
            <span>{isActive ? 'ใช้งาน' : 'ไม่ใช้งาน'}</span>
          </div>
        );
      },
    },
    {
      key: 'phone',
      title: 'เบอร์โทร',
      width: '110px',
      sortable: true,
      render: (value: string) => (
        <span className="font-mono text-sm">
          {value || '-'}
        </span>
      ),
    },
    {
      key: 'email',
      title: 'อีเมล',
      width: '180px',
      sortable: true,
      render: (value: string) => (
        value ? (
          <a
            href={`mailto:${value}`}
            className="inline-flex items-center text-xs text-primary-600 hover:text-primary-700 truncate group"
            title={value}
          >
            <span className="truncate max-w-[140px] group-hover:underline">{value}</span>
            <svg className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        ) : <span className="text-secondary-400">-</span>
      ),
    },
    {
      key: 'createdAt',
      title: 'วันที่สร้าง',
      width: '140px',
      sortable: true,
      render: (value: string | null) => formatDateTimeFull(value)
    },
    {
      key: 'updatedAt',
      title: 'อัพเดทล่าสุด',
      width: '140px',
      sortable: true,
      render: (value: string | null) => formatDateTimeFull(value)
    },
    {
      key: 'depositSettings',
      title: 'ค่ามัดจำ',
      width: '120px',
      sortable: true,
      render: (value: any, record: Company) => {
        const depositAmount = record.deposit_amount || 0;

        return (
          <div className="text-right">
            {depositAmount > 0 ? (
              <div className="inline-flex items-center gap-1 px-2 py-1 bg-accent-50 rounded-lg border border-accent-200/50">
                <span className="font-semibold text-sm text-accent-700">
                  {depositAmount.toLocaleString()}
                </span>
                <span className="text-xs text-accent-500">฿</span>
              </div>
            ) : (
              <span className="text-secondary-400 text-xs">-</span>
            )}
          </div>
        );
      },
    },
    {
      key: 'actions',
      title: 'การจัดการ',
      width: '100px',
      sticky: true,
      render: (_: any, record: Company) => (
        <ActionDropdown
          actions={[
            ...(canView('companies') ? [{
              label: 'ดูรายละเอียด',
              icon: <RiEyeLine className="w-4 h-4" />,
              onClick: () => openCompanyPage(companyDetailUrl(record.id))
            }] : []),
            ...(canEdit('companies') ? [{
              label: 'แก้ไข',
              icon: <RiEditLine className="w-4 h-4" />,
              onClick: () => openCompanyPage(companyEditUrl(record.id))
            }] : []),
            // ลิงก์ข้ามโมดูล (G-12: ต้องมีสิทธิ์ปลายทาง quotations.view) — หน้ารายการรองรับ ?company= อยู่แล้ว
            ...(hasPermission('quotations.view') ? [{
              label: 'ใบเสนอราคาของบริษัทนี้',
              icon: <RiFileTextLine className="w-4 h-4" />,
              onClick: () => navigate(`/billing/quotations?company=${record.id}`)
            }] : []),
            ...(canDelete('companies') ? [{
              divider: true
            },
            {
              label: 'ลบ',
              icon: <RiDeleteBinLine className="w-4 h-4" />,
              onClick: () => handleDeleteCompany(record),
              className: 'text-danger-600 hover:text-danger-700 hover:bg-danger-50'
            }] : [])
          ]}
          buttonIcon={
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          }
          buttonLabel=""
        />
      )
    }
  ];
  
  // ไม่ต้องใช้ฟุตเตอร์สำหรับ Modal ลบบริษัทอีกต่อไป เพราะใช้ showConfirm แทน
  
  // ส่วนหัวของหน้า
  const renderPageHeader = () => {
    return (
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0 shadow-sm">
            <RiBuildingLine className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-secondary-900 leading-tight">ข้อมูลบริษัท</h1>
            <p className="text-xs text-secondary-500 mt-0.5">จัดการข้อมูลบริษัทลูกค้าและพาร์ทเนอร์ในระบบ</p>
          </div>
        </div>
        {companyStats.total > 0 && (
          <span className="inline-flex items-center px-3 min-h-[44px] sm:min-h-[32px] py-1.5 rounded-lg bg-secondary-100 text-secondary-600 text-xs font-medium self-start sm:self-auto">
            {companyStats.total.toLocaleString()} รายการ
          </span>
        )}
      </div>
    );
  };
  
  return (
    <Layout>
      <div className="w-full">
        {/* Page Header */}
        {renderPageHeader()}

        {/* แสดง Error Message ถ้ามีข้อผิดพลาด */}
        {error && (
          <div className="mb-4 p-4 border border-danger-200 rounded-lg bg-danger-50 text-danger-700">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-danger-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm">เกิดข้อผิดพลาดในการโหลดข้อมูล</p>
                <div className="mt-2">
                  <button
                    type="button"
                    className="text-sm font-medium text-danger-700 hover:text-danger-600 focus:outline-none"
                    onClick={() => refetch()}
                  >
                    ลองใหม่อีกครั้ง
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons Row - Enhanced Design */}
        <div className="mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <CanCreate module="companies">
              <button
                onClick={() => openCompanyPage(`${COMPANIES_PATH}/new`)}
                className="group inline-flex items-center px-5 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 shadow-button transition-all duration-200"
              >
                <RiBuildingLine className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform duration-300" />
                เพิ่มบริษัทใหม่
              </button>
            </CanCreate>
            <button
              onClick={handleRefresh}
              className="group inline-flex items-center px-4 min-h-[44px] sm:min-h-[42px] py-2.5 bg-white border border-secondary-200 text-secondary-700 text-sm font-medium rounded-lg hover:bg-secondary-50 hover:border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 shadow-button transition-all duration-200"
            >
              <RiRefreshLine className="w-5 h-5 mr-2 group-hover:rotate-180 transition-transform duration-500" />
              รีเฟรช
            </button>
          </div>

          <div className="flex items-center gap-2">
            {/* ปิดปุ่มซิงค์ Flow Account ชั่วคราว
            {hasPermission('companies.sync') && (
              <button
                type="button"
                className="group inline-flex items-center px-4 min-h-[44px] sm:min-h-[42px] py-2.5 bg-info-600 text-white text-sm font-medium rounded-md hover:bg-info-700 focus:outline-none focus:ring-2 focus:ring-info-500 focus:ring-offset-2 shadow-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleSyncWithFlowAccount}
                disabled={syncLoading}
              >
                <RiCloudLine className={`w-5 h-5 mr-2 ${syncLoading ? 'animate-spin' : 'group-hover:scale-110 transition-transform'}`} />
                ซิงค์ Flow Account
              </button>
            )}
            */}
            {canExport('companies') && (
              <button
                onClick={handleExportExcel}
                disabled={companies.length === 0}
                className="inline-flex items-center px-4 min-h-[44px] sm:min-h-[42px] py-2.5 bg-white border border-secondary-200 text-secondary-700 text-sm font-medium rounded-lg hover:bg-secondary-50 hover:border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 shadow-button transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
              >
                <RiFileDownloadLine className="w-5 h-5 mr-2 text-success-600" />
                ส่งออก Excel
              </button>
            )}
          </div>
        </div>

        {/* Compact Stats Bar */}
        {companyStats.total > 0 && !isLoading && (
          <div className="mb-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white rounded-xl border border-secondary-200 px-4 py-3 shadow-sm">
              <p className="text-xs text-secondary-500 font-medium">ทั้งหมด</p>
              <p className="text-lg font-bold text-secondary-900 tabular-nums">{companyStats.total.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-xl border border-success-200 px-4 py-3 shadow-sm">
              <p className="text-xs text-success-700 font-medium">ใช้งาน</p>
              <p className="text-lg font-bold text-success-700 tabular-nums">{companyStats.active.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-xl border border-secondary-200 px-4 py-3 shadow-sm">
              <p className="text-xs text-secondary-500 font-medium">ไม่ใช้งาน</p>
              <p className="text-lg font-bold text-secondary-600 tabular-nums">{companyStats.inactive.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-xl border border-info-200 px-4 py-3 shadow-sm">
              <p className="text-xs text-info-600 font-medium">Sync Flow</p>
              <p className="text-lg font-bold text-info-700 tabular-nums">{companyStats.synced.toLocaleString()}</p>
            </div>
          </div>
        )}

        {/* Search & Filter Card */}
        <SearchFilterCard
          searchTerm={searchTerm}
          onSearchChange={(value) => {
            setSearchTerm(value);
            setCurrentPage(1);
          }}
          searchPlaceholder="ค้นหารหัส/ชื่อบริษัท/เลขผู้เสียภาษี..."
          filters={filters}
          filterValues={filterValues}
          onFilterChange={handleFilterChange}
          onClearAll={handleClearAllFilters}
          showFilterTags={true}
          customButtons={
            <button
              onClick={advancedSearch.openSearchModal}
              className={`inline-flex items-center h-11 sm:h-[42px] px-3 border shadow-sm text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors ${
                advancedSearch.hasActiveFilters
                  ? 'border-primary-300 text-primary-700 bg-primary-50 hover:bg-primary-100'
                  : 'border-secondary-300 text-secondary-700 bg-white hover:bg-secondary-50'
              }`}
            >
              <RiFilterLine className="w-4 h-4 mr-1" />
              ค้นหาขั้นสูง
              {advancedSearch.hasActiveFilters && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary-200 text-primary-800 rounded-full">
                  {advancedSearch.getActiveFilterCount()}
                </span>
              )}
            </button>
          }
        />

        {/* Active Advanced Filters Display */}
        {advancedSearch.hasActiveFilters && (
          <div className="mb-4 p-4 bg-info-50 border border-info-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-info-900">ตัวกรองขั้นสูงที่ใช้งาน</h3>
              <button
                onClick={advancedSearch.handleClearFilters}
                className="text-xs text-info-600 hover:text-info-800"
              >
                ล้างทั้งหมด
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {advancedSearch.getFilterSummary().map((filter, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 text-xs bg-info-100 text-info-800 rounded-full"
                >
                  {filter}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Bulk Actions Bar */}
        <BulkActionsBar
          selectedCount={selectedCompanies.length}
          onBulkDelete={hasPermission('companies.delete') ? handleBulkDelete : undefined}
          onClearSelection={() => setSelectedCompanies([])}
          loading={bulkLoading}
        />

        {/* Table component */}
        <Table
          columns={canDelete('companies') ? columns : columns.filter(c => c.key !== 'select')}  /* KD3 S2122 (BUG-S2122-004): ไม่มีสิทธิ์ลบ = ไม่มีช่องติ๊ก (ติ๊กแล้วไม่มีอะไรให้ทำ) */
          data={companies}
          emptyText={error ? 'โหลดข้อมูลไม่สำเร็จ' : undefined}
          emptyHint={error ? null : undefined}  /* BUG-S2273-001: โหลดล้ม ≠ ตัวกรองไม่เจอ */
          /* KD3 S2494 (NEG-MD-015): loading เดิมมีแต่ mutation pending — เน็ตช้าตอนโหลดรายการจึงขึ้น
             "ไม่มีข้อมูล ลองปรับเงื่อนไขการค้นหา" แทนสถานะกำลังโหลด ผู้ใช้เข้าใจว่าไม่มีบริษัท */
          loading={loading || isLoading}
          pagination={false}
          searchable={false}
          sortable={true}
        />
        
        {/* Pagination */}
        {!loading && companies.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setCurrentPage(1);
            }}
            showPageSizeSelector={true}
            showSummary={true}
            className="mt-5"
          />
        )}
        
        {/* Advanced Search Modal */}
        <AdvancedSearchModal
          isOpen={advancedSearch.isSearchModalOpen}
          onClose={advancedSearch.closeSearchModal}
          onSearch={advancedSearch.handleSearch}
          onSaveFilter={advancedSearch.handleSaveFilter}
          onDeleteFilter={advancedSearch.handleDeleteFilter}
          searchFields={searchFields}
          savedFilters={advancedSearch.savedFilters}
          currentFilters={advancedSearch.currentFilters}
          title="ค้นหาบริษัทขั้นสูง"
          loading={loading}
        />
        
        {/* ไม่ต้องใช้ Modal สำหรับยืนยันการลบบริษัทอีกต่อไป เพราะใช้ showConfirm แทน */}

        {/* Loading Overlay for Export */}
        <LoadingOverlayModal
          isOpen={isExporting}
          icon="export"
          title="กำลังส่งออกข้อมูล"
          submessage="กรุณารอสักครู่ ระบบกำลังเตรียมไฟล์ Excel"
        />
      </div>
    </Layout>
  );
};

export default CompanyPage;